import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Mail, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import api from '../../utils/api';

export const meta = {
  id: 'email_health',
  category: 'system',
  titleKey: 'dashboard.widgets.emailHealth',
  descriptionKey: 'dashboard.widgets.emailHealthDesc',
  defaultSize: 'sm',
  requiresModule: 'comms_monitor',
  icon: Mail,
};

const STATUS_META = {
  green: { color: '#22c55e', Icon: CheckCircle2, labelKey: 'dashboard.widgets.emailHealthGreen' },
  yellow: { color: '#f59e0b', Icon: AlertTriangle, labelKey: 'dashboard.widgets.emailHealthYellow' },
  red: { color: '#ef4444', Icon: XCircle, labelKey: 'dashboard.widgets.emailHealthRed' },
};

export default function EmailHealthWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['comms-health'],
    queryFn: () => api.get('/comms-health').then(r => r.data),
    refetchInterval: 5 * 60 * 1000,
  });

  const status = data?.status || 'green';
  const lastCheck = data?.lastCheck;
  const m = STATUS_META[status] || STATUS_META.green;
  const Icon = m.Icon;

  const inbound = parseInt(lastCheck?.inbound_count_1h) || 0;
  const outbound = parseInt(lastCheck?.outbound_count_1h) || 0;
  const failed = parseInt(lastCheck?.failed_count_1h) || 0;

  return (
    <div className="card" style={{ cursor: 'pointer', height: '100%' }} onClick={() => navigate('/comms-monitor')}>
      <div className="card-header">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Mail size={16} />
          {t('dashboard.widgets.emailHealth')}
        </div>
      </div>
      <div style={{ padding: '8px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <Icon size={28} color={m.color} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: m.color }}>
              {t(m.labelKey)}
            </div>
            {lastCheck?.checked_at && (
              <div style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>
                {new Date(lastCheck.checked_at).toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Row label={t('dashboard.widgets.emailInbound1h', 'Inbound 1h')} value={inbound} />
          <Row label={t('dashboard.widgets.emailOutbound1h', 'Outbound 1h')} value={outbound} />
          <Row label={t('dashboard.widgets.emailFailed1h', 'Failed 1h')} value={failed} valueColor={failed > 0 ? '#ef4444' : undefined} />
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, valueColor }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: valueColor || 'var(--c-text)' }}>{value}</span>
    </div>
  );
}
